import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../lib/api/auth';
import type { ApiError } from '../lib/api';
// PWA install prompt removed per Issue #606
import Button from '../components/Button';
import Card from '../components/Card';

export default function MagicLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [error, setError] = useState<string | null>(null);
  const [redirectTarget, setRedirectTarget] = useState<string>('your clubs');
  
  // Use ref to track execution state that persists across effect runs
  const hasExecutedRef = useRef(false);
  const isExecutingRef = useRef(false);
  
  // Timeout cleanup refs (Frank's feedback)
  const installTimeoutRef = useRef<number | null>(null);
  const redirectTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setError('No token provided');
      return;
    }
    
    // Early return if already executed
    if (hasExecutedRef.current) return;

    const consumeToken = async () => {
      // Prevent concurrent or duplicate executions using refs
      if (isExecutingRef.current) {
        return;
      }
      
      isExecutingRef.current = true;

      try {
        const response = await authApi.consumeMagicLink(token);
        
        // Mark as successfully executed
        hasExecutedRef.current = true;
        
        // Invalidate the ['me'] query to refetch current user
        await queryClient.invalidateQueries({ queryKey: ['me'] });
        
        // Check if user needs onboarding (missing name fields)
        // No type casting needed - response.user is correctly typed as Me (George's feedback)
        const { user } = response;
        const needsOnboarding = !user.first_name || !user.last_name;
        const isPlatformAdmin = user.is_platform_admin;
        
        // Set redirect target for success message
        if (needsOnboarding) {
          setRedirectTarget('your profile setup');
        } else if (isPlatformAdmin) {
          setRedirectTarget('the admin dashboard');
        } else {
          setRedirectTarget('your clubs');
        }
        
        // Set success status only after we're ready to redirect
        setStatus('success');
        
        // PWA install prompt removed per Issue #606
        // Focus on mobile web experience for v1.0
        
        // Redirect to appropriate page after successful login
        redirectTimeoutRef.current = window.setTimeout(() => {
          if (needsOnboarding) {
            navigate('/onboarding');
          } else if (isPlatformAdmin) {
            navigate('/platform-admin/home');
          } else {
            navigate('/home');
          }
        }, 1500);
      } catch (err) {
        // Only set error state if we haven't already succeeded
        if (!hasExecutedRef.current) {
          // Log error in development only
          if (process.env.NODE_ENV === 'development') {
            console.error('[MagicLogin] Token consumption failed:', err);
          }
          
          // Safely extract error message (Frank's feedback)
          let message = 'Invalid or expired link';
          if (err && typeof err === 'object' && 'message' in err) {
            const errorMessage = (err as ApiError).message;
            if (errorMessage) {
              message = errorMessage;
            }
          }
          
          setStatus('error');
          setError(message);
        }
      } finally {
        isExecutingRef.current = false;
      }
    };

    // Execute immediately - no delay needed with ref-based guards
    consumeToken();

    // Cleanup function to prevent state updates after unmount
    return () => {
      hasExecutedRef.current = true; // Prevent any further executions
      
      // Clear timeouts to prevent post-unmount actions (Frank's feedback)
      if (installTimeoutRef.current !== null) {
        clearTimeout(installTimeoutRef.current);
      }
      if (redirectTimeoutRef.current !== null) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [searchParams, navigate, queryClient]);

  return (
    <div className="max-w-lg mx-auto" aria-live="polite">
      {status === 'verifying' && (
        <Card className="p-12 text-center">
          <svg
            className="oro-loading h-12 w-12 rounded-full bg-accent mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <h2 className="text-xl font-semibold text-text-primary">
            Verifying your login...
          </h2>
        </Card>
      )}

      {status === 'success' && (
        <Card className="text-center p-12">
          <div className="text-green-400 text-5xl mb-4">✓</div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">
            Success!
          </h2>
          <p className="text-text-secondary">
            Redirecting you to {redirectTarget}...
          </p>
        </Card>
      )}

      {status === 'error' && (
        <Card className="text-center p-12">
          <div className="text-red-400 text-5xl mb-4">✕</div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">
            Login Failed
          </h2>
          <p className="text-text-secondary mb-6">
            {error || 'This link is invalid or has expired.'}
          </p>
          <Button
            onClick={() => navigate('/')}
          >
            Back to Home
          </Button>
        </Card>
      )}
    </div>
  );
}
